// Un solo sitio decide en que entorno estamos.
//
// Antes esta decision estaba reimplementada en cada componente como una cadena de
// `hostname === "..."`, con la lista de hosts copiada a mano y un `else` distinto en
// cada sitio. Anadir un entorno obligaba a encontrar las ocho cadenas, y lo que se
// escapara caia a un valor por defecto que en varios casos apuntaba a produccion.
//
// La base de conocimiento se sirve en `knowledgebase.<dominio>`, no en el dominio
// pelado. Es un detalle facil de pasar por alto: una cadena escrita contra
// `excent.capital` no casa nunca aqui.
const HOST_ENV_MAP = {
  'knowledgebase.excent.capital': 'SEY',
  'knowledgebase.excentcapital.com': 'SVG',
  // Produccion nueva (Mexico), cada entidad en su familia de dominio.
  'knowledgebase.mxsey.excent.capital': 'MXSEY',
  'knowledgebase.mxsvg.excentcapital.com': 'MXSVG',
  'knowledgebase.dev.excent.capital': 'DEV',
  'knowledgebase.test.excent.capital': 'TEST',
  'knowledgebase.demo.excent.capital': 'DEMO',
  'support.excentcapital.tech': 'TECH',
  localhost: 'DEV',
  '127.0.0.1': 'DEV',
};

// Para los hosts de la produccion nueva la caida NO llega a produccion: si falta una
// variable el valor sale nulo, que se nota, en vez de mandar el ambiente nuevo a
// Virginia sin dejar rastro.
const FALLBACK_BY_ENV = {
  MXSEY: ['MXSEY'],
  MXSVG: ['MXSVG'],
};

const currentHostname = () =>
  typeof window === 'undefined' ? '' : window.location.hostname;

export const getEnvKeyForHost = (hostname = currentHostname()) =>
  HOST_ENV_MAP[hostname] || null;

// `lastResort` es el `else` que tenia cada cadena antes de centralizarse, y solo entra
// en juego para un host que no este en el mapa.
const pickEnvUrl = (prefix, hostname, lastResort = 'TECH') => {
  const envKey = getEnvKeyForHost(hostname);
  const candidates = envKey
    ? FALLBACK_BY_ENV[envKey] || [envKey, lastResort]
    : [lastResort];

  for (const key of candidates) {
    const value = import.meta.env[`${prefix}_${key}`];
    if (value) return value;
  }
  return null;
};

export const getAccountsSignInUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_ACCOUNTS', hostname);

export const getAccountsSignUpUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_ACCOUNTS_UP', hostname);

export const getTradingUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_TRADING', hostname);

export const getBackofficeUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_BO', hostname);

export const getMainpageUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_MAINPAGE', hostname);

export const getLoginUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_LOGIN', hostname);

export const getLogoutUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_LOGOUT', hostname);

// Notifications.jsx comparaba contra los dominios pelados -- `excent.capital` y
// compania -- que aqui no casan nunca, asi que en todos los entornos, produccion
// incluida, terminaba cayendo al `else` y hablando con el backend de `.tech`. Al pasar
// por el mapa cada entorno usa el suyo.
export const getNotificationsWsUrl = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_NOTIFICATIONS', hostname);

export const getSentryDsn = (hostname = currentHostname()) =>
  pickEnvUrl('VITE_SENTRY_DSN', hostname);

// Sufijo de las cookies de sesion, del mismo mapa que las URLs para que no se separen.
// Produccion usa el nombre pelado; los demas entornos llevan el suyo.
const COOKIE_SUFFIX_BY_ENV = {
  SEY: '',
  SVG: '',
  DEV: '_dev',
  TEST: '_test',
  DEMO: '_demo',
  // Las cadenas originales no le daban sufijo, usaba el nombre pelado.
  TECH: '',
  MXSEY: '_mxsey',
  MXSVG: '_mxsvg',
};

export const getCookieSuffix = (hostname = currentHostname()) =>
  COOKIE_SUFFIX_BY_ENV[getEnvKeyForHost(hostname)] ?? '_test';
